/**
 * Component for BackstageArtist
 *
 * @module component/BackstageArtist
 */

import { List } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { StickyContainer } from 'react-sticky';

import { annotateObjectsForBackstage, getArtistEventsForBackstage } from '../actions';
import { BackstageConstants, BackstageLoggerDefaultArgs, emptyMap, messages } from '../constants';
import * as D from '../deps';
import { getBackstageAnnotation, getCommonBackstageProps, isValidPandoraId } from '../utils';

import BackstageAlbumGrid from './BackstageAlbumGrid';
import BackstageAlbumGridItem from './BackstageAlbumGridItem';
import BackstageFeaturedContent from './BackstageFeaturedContent';
import BackstageHeaderPlayPauseButton from './BackstageHeaderPlayPauseButton';
import BackstageLargeHeader from './BackstageLargeHeader';
import BackstageRowColumns from './BackstageRowColumns';
import BackstageRowGrid from './BackstageRowGrid';
import BackstageStationGridItem from './BackstageStationGridItem';
import { getRadioOnlyModalConfig } from './common/ArtistRadioOnlyModal';
import BackstageLargeHeaderLayout from './layout/BackstageLargeHeaderLayout';
import ArtistBio from './pages/BackstageArtist1/ArtistBio';
import ArtistSidebarButtons from './pages/BackstageArtist1/ArtistSidebarButtons';
import ArtistSplitPane from './pages/BackstageArtist1/ArtistSplitPane';
import ArtistTopSongs from './pages/BackstageArtist1/ArtistTopSongs';
import BackstageArtistGraphQL from './pages/BackstageArtist1/BackstageArtist';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { STARTSTATIONARTIST, SHARE } = D.Constants.SourceCard.SourceCardConstants;
const { INSUFFICIENT_RIGHTS_TOAST } = D.Constants.Rights.RightsConstants;

const BackstageSections = D.Constants.ContentSections.BackstageContentSections;

const visibilityProps = contentSection => {
  return { contentSection };
};

const visibilitySettings = ({ isRowComponent, isGridComponent, useMemo }) => {
  return {
    staticHeight: isRowComponent && D.RowItemHeights.DEFAULT,
    offsets: isGridComponent && {
      bottom: -1 * D.Constants.Styling.GlobalStyleConstants.GRID_ITEM_FOOTER_HEIGHT,
    },
    useMemo,
  };
};

const LatestReleaseGridItemWithLogger = D.withVisibilityLogger(
  visibilityProps(BackstageSections.LATEST_RELEASE),
  visibilitySettings({ isGridComponent: true }),
)(BackstageAlbumGridItem);

const ArtistStationGridItemWithLogger = D.withVisibilityLogger(
  visibilityProps(BackstageSections.ARTIST_RADIO),
  visibilitySettings({ isGridComponent: true }),
)(BackstageStationGridItem);

const TopAlbumsTitleWithLogger = D.withVisibilityLogger(
  visibilityProps(BackstageSections.TOP_ALBUMS),
  {
    offsets: {
      bottom: D.Constants.Styling.GlobalStyleConstants.GRID_ITEM_FOOTER_HEIGHT * 3,
    },
  },
)(FormattedMessage);

const SimilarArtistsWithLogger = D.withVisibilityLogger(
  visibilityProps(BackstageSections.SIMILAR_ARTISTS),
  visibilitySettings({ isRowComponent: true, useMemo: true }),
)(BackstageRowGrid);

// Don't move this into getDefaultProps or it'll be a new element every time props are
// generated and componentDidUpdate can end up un an infinite loop
const descriptionTitleDefault = <FormattedMessage {...messages.biography} />;

// TODO WEBCA-1926: Remove this component and it's referenced utils / consts after curator project launches
export class BackstageArtist extends React.Component {
  static get defaultProps() {
    return {
      descriptionTitle: descriptionTitleDefault,
      featuredContentIds: List(),
    };
  }

  static propTypes = {
    children: PropTypes.element,
    pandoraId: PropTypes.string,
    pandoraType: PropTypes.string,
    artistName: PropTypes.string,
    artistStationFactoryId: PropTypes.string,
    artistImgUrl: PropTypes.string,
    artistListenerCount: PropTypes.number,
    hasEvents: PropTypes.bool,
    description: PropTypes.string,
    descriptionTitle: PropTypes.object,
    similarArtists: ImmutablePropTypes.listOf(PropTypes.string),
    artistAlbumCount: PropTypes.number,
    artistTopAlbums: ImmutablePropTypes.listOf(PropTypes.string),
    artistTopTracks: ImmutablePropTypes.listOf(PropTypes.string),
    annotateObjectsForBackstage: PropTypes.func.isRequired,
    getArtistEventsForBackstage: PropTypes.func.isRequired,
    latestReleasePandoraId: PropTypes.string,
    startStation: PropTypes.func.isRequired,
    isListener: PropTypes.bool,
    artistTwitterHandle: PropTypes.string,
    isCurrentArtistStationPlaying: PropTypes.bool,
    nowPlayingUrl: PropTypes.string,
    pauseTrack: PropTypes.func,
    openShareModal: PropTypes.func,
    isRightRailActive: PropTypes.bool,
    encryptedPandoraId: PropTypes.string,
    closeModal: PropTypes.func,
    openModal: PropTypes.func,
    artistTopSongsPandoraId: PropTypes.string,
    hasOnDemandTopSongs: PropTypes.bool,
    isArtistTopSongsPlaying: PropTypes.bool,
    isArtistTopSongsOn: PropTypes.bool,
    hasRadio: PropTypes.bool,
    pageUrl: PropTypes.string,
    shareableUrlPath: PropTypes.string,
    playResumeSource: PropTypes.func.isRequired,
    isMobileWeb: PropTypes.bool,
    disableHover: PropTypes.bool,
    userStationId: PropTypes.string,
    largeHeaderImageUrl: PropTypes.string,
    largeHeaderDominantColor: PropTypes.string,
    useLightTheme: PropTypes.bool,
    isVariousArtists: PropTypes.bool,
    artistPandoraId: PropTypes.string,
    featuredContentIds: ImmutablePropTypes.listOf(PropTypes.string),
    hasFetchedData: PropTypes.bool,
    getCatalogDetails: PropTypes.func,
    isInitialAppLoad: PropTypes.bool,
    graphQLRequest: PropTypes.func,
    isCuratorExperienceEnabled: PropTypes.bool,
  };

  componentDidMount() {
    this.fetchApiData();

    if (isValidPandoraId(this.props.pandoraId)) {
      this.fetchDetails(true);

      if (!this.props.hasEvents) {
        this.props.getArtistEventsForBackstage(this.props.pandoraId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.fetchApiData();
    // Prevent a redundant call using the unencrypted pandoraId, if we already made data calls using the encryptedPandoraId
    const isPandoraIdUpdated = this.props.pandoraId !== this.props.encryptedPandoraId;
    if (
      this.props.pandoraId &&
      prevProps.pandoraId !== this.props.pandoraId &&
      isPandoraIdUpdated
    ) {
      this.fetchDetails(false);
      this.props.getArtistEventsForBackstage(this.props.pandoraId);
    }
  }

  static contextType = D.AppContext;

  getIntegrationData = name => {
    return () => {
      const contexts = {
        artistStation: {
          ...BackstageLoggerDefaultArgs.artistRadio,
          ...{ pandoraId: this.props.artistStationFactoryId },
        },
        latestRelease: {
          ...BackstageLoggerDefaultArgs.latestRelease,
          ...{ pandoraId: this.props.latestReleasePandoraId },
        },
      };
      return contexts[name] || BackstageLoggerDefaultArgs[name];
    };
  };

  // TODO: WEBCA-744 remove once radio-core updates are made
  fetchDetails = redirectOnNotFound => {
    // calling graphql service directly to bypass cache and reliably get the collaborationArtists without additional requests. this ensures
    // collaboration data gets retrieved for direct backstage linking
    const requestParams =
      this.props.pandoraType === D.Constants.SourceTypes.PandoraTypes.COMPOSER
        ? D.GraphQL.Queries.getComposerDetails({
            pandoraId: this.props.pandoraId,
          })
        : D.GraphQL.Queries.getArtistDetails({
            pandoraId: this.props.pandoraId,
            withCollaborations: true,
          });

    this.props.graphQLRequest(requestParams);
    this.fetchCollaborations();
  };

  fetchApiData = () => {
    const pandoraIds = D.union(
      this.props.similarArtists,
      this.props.artistTopAlbums,
      this.props.artistTopTracks,
      List([this.props.pandoraId]),
      List([this.props.latestReleasePandoraId]),
    );

    this.props.annotateObjectsForBackstage(pandoraIds);
  };

  fetchCollaborations = () => {
    const requestParams = D.GraphQL.Queries.getArtistCollaborations({
      pandoraId: this.props.pandoraId,
    });
    this.props.graphQLRequest(requestParams);
  };

  onModalStartStationClick = () => {
    this.props.closeModal();
    this.playArtistStation();
  };

  displayModal = () => {
    this.props.openModal(
      getRadioOnlyModalConfig({
        hasRadio: this.props.hasRadio,
        onModalStartStationClick: this.onModalStartStationClick,
      }),
    );
  };

  playPauseTopSongs = () => {
    const { hasOnDemandTopSongs, pandoraId } = this.props;

    if (!hasOnDemandTopSongs) {
      // Handle insufficient rights
      this.context.integration.action.clicked({
        ...BackstageLoggerDefaultArgs.controlBar,
        ...{ pandoraId, componentEventType: INSUFFICIENT_RIGHTS_TOAST },
      });
      this.displayModal();
      return;
    }

    if (this.props.isArtistTopSongsPlaying) {
      // Handle pause
      this.context.integration.action.clicked({
        ...BackstageLoggerDefaultArgs.controlBar,
        ...{ pandoraId, componentEventType: PAUSE },
      });
      this.props.pauseTrack();
    } else {
      // Handle play, Premium Access
      this.context.integration.action.clicked({
        ...BackstageLoggerDefaultArgs.controlBar,
        ...{ pandoraId, componentEventType: AUDIO_PLAY_TRACK },
      });
      this.props.playResumeSource({
        id: this.props.artistTopSongsPandoraId,
        pandoraType: D.PandoraTypes.ARTIST_TOP_SONGS,
        context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
        pageUrl: this.props.shareableUrlPath,
      });
    }
  };

  playArtistStation = () => {
    const { pandoraId, isCurrentArtistStationPlaying, nowPlayingUrl } = this.props;
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: STARTSTATIONARTIST },
    });

    if (isCurrentArtistStationPlaying) {
      // TODO: This might be better in the startStation use case, if we want clicking play on
      // a playing artist station to always route to NP
      this.context.router.push(nowPlayingUrl);
    } else {
      this.props.startStation({
        pandoraId,
        rerouteBasedOnTier: true,
        componentName: 'BackstageArtist',
        pageUrl: this.props.shareableUrlPath,
      });
    }
  };

  handleShareOnClick = () => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: SHARE },
    });
    this.props.openShareModal(null, {
      shareParentSourceId: this.props.artistPandoraId,
      shareParentSourceType: D.Constants.SourceTypes.PandoraTypes.ARTIST,
    });
  };

  onSeeAllAlbumsClick = e => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.topAlbums,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  renderStructuredMetaData = () => {
    const { pandoraId } = this.props;

    if (!pandoraId) return;

    return (
      <D.StructuredMetadata
        isArtist={true}
        artistName={this.props.artistName}
        pandoraId={pandoraId}
        twitterHandle={this.props.artistTwitterHandle}
        description={this.props.description}
        image={this.props.artistImgUrl}
      />
    );
  };

  renderSideBarButtons = () => {
    return (
      <ArtistSidebarButtons
        playTopSongs={this.playPauseTopSongs}
        pauseTopSongs={this.playPauseTopSongs}
        isArtistTopSongsPlaying={this.props.isArtistTopSongsPlaying}
        playArtistStation={this.playArtistStation}
        shareArtist={this.handleShareOnClick}
        topSongsHaveNoRights={!this.props.hasOnDemandTopSongs}
        hasRadio={this.props.hasRadio}
      />
    );
  };

  renderAlbumCount = () => {
    const { artistAlbumCount, artistTopAlbums } = this.props;

    if (!artistAlbumCount || !artistTopAlbums || (artistTopAlbums && artistTopAlbums.isEmpty())) {
      return;
    }

    const abbreviatedAlbumCount = D.numberToHumanSize(artistAlbumCount);
    return (
      <span>
        {abbreviatedAlbumCount}{' '}
        <FormattedMessage {...messages.albums} values={{ albumCount: artistAlbumCount }} />
      </span>
    );
  };

  renderArtistBio = () => {
    const { pandoraId, shareableUrlPath, largeHeaderImageUrl, description, isMobileWeb } =
      this.props;

    const shortTextWidthRatio = largeHeaderImageUrl || isMobileWeb ? 4 : null;

    return (
      <ArtistBio
        pandoraId={pandoraId}
        bio={description}
        shareableUrlPath={shareableUrlPath}
        isMobileDevice={isMobileWeb}
        shortTextWidthRatio={shortTextWidthRatio}
      />
    );
  };

  renderArtistEvents = () => {
    if (!this.props.hasEvents) {
      return null;
    }
    return (
      <D.ContentSection isArtistEvents={true}>
        <D.ArtistEvents artistPandoraId={this.props.pandoraId} />
      </D.ContentSection>
    );
  };

  renderArtistTopSongs = () => {
    return (
      <ArtistTopSongs
        topTracks={this.props.artistTopTracks || emptyMap}
        artistPlayId={this.props.artistTopSongsPandoraId}
        shareableUrlPath={this.props.shareableUrlPath}
        isLoading={!this.props.hasFetchedData}
      />
    );
  };

  renderFeaturedContent = () => {
    const { featuredContentIds } = this.props;
    if (featuredContentIds.size > 0) {
      return <BackstageFeaturedContent featuredContentIds={featuredContentIds} />;
    }
    return null;
  };

  renderArtistStationGridItem = () => {
    const { pandoraId, userStationId } = this.props;
    if (!pandoraId || !this.props.hasFetchedData || this.props.hasRadio === false) return;

    return (
      <D.AppContextIntegrationData data={this.getIntegrationData('artistStation')}>
        <D.ContentSection
          isArtistStation={true}
          title={<FormattedMessage {...messages.artistRadio} />}
        >
          <ArtistStationGridItemWithLogger
            id={userStationId}
            pandoraId={pandoraId}
            isMobileWeb={this.props.isMobileWeb}
            pageUrl={this.props.shareableUrlPath}
            twoColumns={!this.props.isRightRailActive}
            isFluid={true}
          />
        </D.ContentSection>
      </D.AppContextIntegrationData>
    );
  };

  renderLatestReleaseGridItem = () => {
    if (!this.props.latestReleasePandoraId) return;

    return (
      <D.AppContextIntegrationData data={this.getIntegrationData('latestRelease')}>
        <D.ContentSection
          isLatestRelease={true}
          title={<FormattedMessage {...messages.latestRelease} />}
        >
          <LatestReleaseGridItemWithLogger
            pandoraId={this.props.latestReleasePandoraId}
            twoColumns={!this.props.isRightRailActive}
            isFluid={true}
          />
        </D.ContentSection>
      </D.AppContextIntegrationData>
    );
  };

  renderStationAndLatestRelease = () => {
    return (
      <ArtistSplitPane
        left={this.renderLatestReleaseGridItem()}
        right={this.renderArtistStationGridItem()}
        isRightRailActive={this.props.isRightRailActive}
      />
    );
  };

  renderTopAlbums = () => {
    const { artistTopAlbums } = this.props;
    if (!artistTopAlbums || artistTopAlbums.isEmpty()) return;

    const NUM_TOP_ALBUMS_TO_RENDER = this.props.isMobileWeb
      ? BackstageConstants.NUM_ITEMS_PER_ROW
      : BackstageConstants.NUM_ITEMS_PER_ROW_LARGE;
    let moreText;
    let moreUrl;

    if (this.props.artistAlbumCount > NUM_TOP_ALBUMS_TO_RENDER) {
      moreText = <FormattedMessage {...messages.seeAllAlbums} />;
      moreUrl = D.getBackstageArtistAllAlbumsPathForShareUrl(this.props.shareableUrlPath);
    }

    return (
      <D.AppContextIntegrationData data={this.getIntegrationData('topAlbums')}>
        <D.ContentSection
          isGridList={true}
          title={<TopAlbumsTitleWithLogger {...messages.topAlbums} />}
          moreTextOnclick={this.onSeeAllAlbumsClick}
          moreText={moreText}
          moreUrl={moreUrl}
          disableHover={this.props.disableHover}
        >
          <BackstageAlbumGrid
            pandoraIds={artistTopAlbums}
            numPlaceholders={NUM_TOP_ALBUMS_TO_RENDER}
          />
        </D.ContentSection>
      </D.AppContextIntegrationData>
    );
  };

  renderSimilarArtists = () => {
    let similarArtists = this.props.similarArtists;

    if (!similarArtists || similarArtists.isEmpty()) return;

    if (this.props.isMobileWeb) {
      similarArtists = similarArtists.setSize(BackstageConstants.NUM_SIMILAR_ARTISTS_TO_RENDER);
    }

    return (
      <D.AppContextIntegrationData data={this.getIntegrationData('similarArtists')}>
        <D.ContentSection
          isSimilarArtists={true}
          title={<FormattedMessage {...messages.similarArtists} />}
        >
          <SimilarArtistsWithLogger>
            {similarArtists.map(pandoraId => (
              <BackstageRowColumns pandoraId={pandoraId} key={pandoraId} />
            ))}
          </SimilarArtistsWithLogger>
        </D.ContentSection>
      </D.AppContextIntegrationData>
    );
  };

  onTwitterButtonClick = () => {
    window.open(this.props.artistTwitterHandle, 'twitter').focus();
  };

  renderTwitterLink = () => {
    const twitterHandle = this.props.artistTwitterHandle;

    if (!twitterHandle) return null;

    return (
      <D.ContentSection
        isSocial={true}
        title={<FormattedMessage {...messages.social} />}
        onSocialButtonClick={this.onTwitterButtonClick}
        disableHover={this.props.disableHover}
      />
    );
  };

  renderMainContent = () => {
    const childrenProps = {
      hasLargeHeader: this.props.largeHeaderImageUrl,
      ...this.props,
    };
    if (this.props.children) {
      return React.cloneElement(this.props.children, childrenProps);
    }
    return (
      <React.Fragment>
        {!this.props.largeHeaderImageUrl && this.renderArtistBio()}
        {this.renderArtistEvents()}
        {this.renderStationAndLatestRelease()}
        {this.renderArtistTopSongs()}
        {this.renderFeaturedContent()}
        {this.renderTopAlbums()}
        {this.renderSimilarArtists()}
        {this.renderTwitterLink()}
      </React.Fragment>
    );
  };

  render() {
    const headerProps = {
      title: this.props.artistName || '',
      titleUrl: this.props.shareableUrlPath,
      disableHover: this.props.disableHover,
      subTitleLrg: D.getInDomain('catalog.utils.displayMonthlyListenerCount')(
        this.props.artistListenerCount,
      ),
      subTitleSm: this.props.isCuratorExperienceEnabled ? null : this.renderAlbumCount(),
      isHeaderInSidebar: this.props.isCuratorExperienceEnabled,
    };

    const playButtonIsDisabled =
      !this.props.artistTopTracks || this.props.artistTopTracks.isEmpty();
    const playButtonLooksDisabled = !playButtonIsDisabled && !this.props.hasOnDemandTopSongs;
    const playPauseButtonIconType = this.props.isArtistTopSongsPlaying ? 'pause' : 'play';
    const playPauseButtonProps = {
      theme: D.ButtonT3Theme.OPAQUE_WHITE,
      isPillShaped: true,
      isDisabled: playButtonIsDisabled,
      looksDisabled: playButtonLooksDisabled,
      iconType: playPauseButtonIconType,
      onClick: this.playPauseTopSongs,
    };

    if (this.props.largeHeaderImageUrl) {
      const LargeHeader = (
        <BackstageLargeHeader
          largeHeaderImageUrl={this.props.largeHeaderImageUrl}
          name={this.props.artistName}
          dominantColor={this.props.largeHeaderDominantColor}
          useLightTheme={this.props.useLightTheme}
          listenerCount={D.getInDomain('catalog.utils.displayMonthlyListenerCount')(
            this.props.artistListenerCount,
          )}
          playPauseButton={<BackstageHeaderPlayPauseButton {...playPauseButtonProps} />}
        />
      );

      return (
        <StickyContainer>
          <BackstageLargeHeaderLayout
            heroImageIsCircle={true}
            headerBackstage={<D.HeaderBackstageWithLogger {...headerProps} />}
            heroImageUrl={this.props.artistImgUrl}
            heroImageLinkUrl={this.props.shareableUrlPath}
            renderSidebarButtons={!this.props.isMobileWeb && this.renderSideBarButtons}
            renderMainContent={this.renderMainContent}
            entityIdentifier={this.props.pandoraId}
            renderArtworkPlayButton={this.props.isMobileWeb}
            playOnClick={this.playPauseTopSongs}
            largeHeader={LargeHeader}
            artistBio={this.renderArtistBio()}
          />
        </StickyContainer>
      );
    }

    return (
      <D.BackstageLayout
        headerProps={headerProps}
        heroImageUrl={this.props.artistImgUrl}
        heroImageLinkUrl={this.props.shareableUrlPath}
        heroImageIsCircle={true}
        renderSidebarButtons={!this.props.isMobileWeb && this.renderSideBarButtons}
        renderMainContent={this.renderMainContent}
        entityIdentifier={this.props.pandoraId}
        renderArtworkPlayButton={this.props.isMobileWeb}
        playOnClick={this.playPauseTopSongs}
        isHeaderInSidebar={this.props.isCuratorExperienceEnabled}
        {...this.props}
      />
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const encryptedPandoraId = ownProps && ownProps.params && ownProps.params.pandoraId;
  const annotation = getBackstageAnnotation(state, encryptedPandoraId);

  if (
    !annotation ||
    annotation.isEmpty() ||
    ![D.PandoraTypes.ARTIST, D.PandoraTypes.COMPOSER].includes(annotation.get('type'))
  ) {
    return {
      pandoraId: encryptedPandoraId ? encryptedPandoraId : null,
    };
  }

  const pandoraId = annotation.get('pandoraId');
  const catalogDetails = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(
    state,
    pandoraId,
  );
  const props = getCommonBackstageProps(state, encryptedPandoraId, pandoraId);
  const pathname = D.getCurrentPathnameFromState(state);
  props.pandoraType = annotation.get('type', D.Constants.SourceTypes.PandoraTypes.ARTIST);
  props.artistPandoraId = pandoraId;
  props.isCuratorExperienceEnabled = D.Utils.TunerVars.curatorExperienceEnabled(state);

  if (catalogDetails && !catalogDetails.isEmpty() && annotation) {
    const isAudioPlaying = D.getInDomain('playback.utils.isAudioPlaying', false);
    const isPlaylistOn = D.getInDomain('playback.utils.isSourcePlayingCurrently', false);
    props.shareableUrlPath = annotation.get('shareableUrlPath');
    props.hasRadio = annotation.get('hasRadio');
    props.artistStationFactoryId = annotation.get('stationFactoryId');
    props.artistName = annotation.get('name');
    const getArtistEventsFromState = D.getInDomain('catalog.utils.getArtistEventsFromState');
    const events = getArtistEventsFromState(state, pandoraId);
    props.hasEvents = !events.isEmpty();
    props.similarArtists = catalogDetails.get('similarArtists');
    props.artistTwitterHandle = catalogDetails.get('twitterUrl');
    props.userStationId = D.getInDomain('playback.utils.getStationIdFromStationFactoryId', null)(
      state,
      props.artistStationFactoryId,
    );
    props.userStationId =
      props.userStationId ||
      D.getInDomain('playback.utils.getStationIdFromSeedPandoraId', null)(state, pandoraId);
    props.isCurrentArtistStationPlaying =
      props.userStationId && isPlaylistOn(state, props.userStationId);
    props.nowPlayingUrl = D.getStationPlayPathForStationCode(props.userStationId);
    props.artistTopAlbums = catalogDetails.get('topAlbums');
    props.artistTopTracks = catalogDetails.get('topTracks');
    props.description = catalogDetails.get('bio');
    props.artistListenerCount = catalogDetails.get('stationListenerCount');
    props.latestReleasePandoraId = catalogDetails.get('latestRelease');
    props.featuredContentIds = catalogDetails.get('featuredBy');
    props.largeHeaderImageUrl = D.Utils.Art.getLargeHeader(state, catalogDetails);
    props.largeHeaderDominantColor = catalogDetails.getIn(['largeHeaderImage', 'dominantColor']);
    props.useLightTheme = !D.Utils.Art.isColorDark(props.largeHeaderDominantColor);
    props.artistTopSongsPandoraId = catalogDetails.get('artistPlayId');
    const isArtistTopSongsOn = isPlaylistOn(state, props.artistTopSongsPandoraId);
    props.isArtistTopSongsOn = isArtistTopSongsOn;
    props.isArtistTopSongsPlaying = isAudioPlaying(state) && isArtistTopSongsOn;

    const artistImg = D.getArt(annotation, 300, state);
    props.artistImgUrl = artistImg ? artistImg : D.getHero(state, catalogDetails, 300);
    props.artistAlbumCount = catalogDetails.get('albumCount');

    if (props.artistTopTracks) {
      const annotatedTopSongs = D.getManyAnnotationsById(state, props.artistTopTracks);
      props.hasOnDemandTopSongs = annotatedTopSongs.some(annotatedTopSong =>
        D.hasInteractiveRights(annotatedTopSong),
      );
    }

    props.isVariousArtists = D.getInDomain('catalog.utils.isVariousArtists')(annotation);
    props.hasFetchedData = true;
  } else {
    props.hasFetchedData = false;
  }

  props.isArtistBackstageUrqlEnabled = D.Utils.TunerVars.isArtistBackstageUrqlEnabled(state);
  props.isRightRailActive =
    D.getInDomain('ads.utils.getIsRightRailActive', false)(state) ||
    D.getInDomain('ads.utils.getIsElementCenteredWithAds', false)(state);
  props.isMobileWeb = D.isMobileWeb(state);
  props.disableHover = D.disableHoverStates(state);
  props.currentPathname = D.getCurrentPathnameFromState(state);
  props.isArtistBasePath = D.Utils.Route.isBackstageArtistPath(pathname);
  props.isInitialAppLoad = D.getInDomain('application.utils.isInitialLoad', false)(state);

  return props;
}

const mapDispatchToProps = {
  openModal: D.openModal,
  closeModal: D.closeModal,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  startStation: D.Actions.UseCases.startStation,
  openShareModal: D.Actions.Share.openShareModal,
  playResumeSource: D.playResumeSource,
  annotateObjectsForBackstage,
  getArtistEventsForBackstage,
  graphQLRequest: D.Services.GraphQL.graphQLRequest,
  getCatalogDetails: D.getInDomain('catalog.actions.getDetails', () => {}),
};

const BackstageArtistBase = props => {
  const { isArtistBasePath, isInitialAppLoad } = props;

  if (isInitialAppLoad) {
    return <BackstageArtist {...props} />;
  } else if (isArtistBasePath) {
    return <BackstageArtistGraphQL {...props} />;
  }
  return <BackstageArtist {...props} />;
};

BackstageArtistBase.propTypes = {
  isArtistBackstageUrqlEnabled: PropTypes.bool,
  isArtistBasePath: PropTypes.bool,
  isInitialAppLoad: PropTypes.bool,
  pandoraId: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageArtistBase);
